if (!customElements.get('flavour-add-to-cart')) {
  customElements.define(
    'flavour-add-to-cart',
    class FlavourAddToCart extends HTMLElement {
      constructor() {
        super();
        this.quantities = {};
        this.activeId = null;
        this.currency = this.dataset.currency || 'GBP';
        this.locale = this.dataset.locale || undefined;
      }

      connectedCallback() {
        this.grid = this.querySelector('[data-flavour-grid]');
        this.cards = Array.from(this.querySelectorAll('[data-flavour-card]'));
        this.selectedSwatch = this.querySelector('[data-selected-swatch]');
        this.selectedName = this.querySelector('[data-selected-name]');
        this.qtyValue = this.querySelector('[data-qty-value]');
        this.qtyMinus = this.querySelector('[data-qty-minus]');
        this.qtyPlus = this.querySelector('[data-qty-plus]');
        this.totalValue = this.querySelector('[data-total-value]');
        this.submitBtn = this.querySelector('[data-flavour-submit]');
        this.errorEl = this.querySelector('[data-flavour-error]');
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');

        this.cards.forEach((card) => {
          card.addEventListener('click', () => this.onCardClick(card));
        });

        this.qtyMinus?.addEventListener('click', () => this.changeQty(-1));
        this.qtyPlus?.addEventListener('click', () => this.changeQty(1));
        this.submitBtn?.addEventListener('click', () => this.addToCart());

        const firstAvailable = this.cards.find((card) => card.dataset.available === 'true');
        if (firstAvailable) {
          this.selectFlavour(firstAvailable.dataset.variantId, true);
        } else {
          this.render();
        }
      }

      onCardClick(card) {
        if (card.dataset.available !== 'true') return;
        const id = card.dataset.variantId;

        // Click again on a selected flavour → deselect (do not keep in cart selection)
        if (this.quantities[id]) {
          delete this.quantities[id];
          if (this.activeId === id) {
            const remaining = Object.keys(this.quantities);
            this.activeId = remaining[remaining.length - 1] || null;
          }
        } else {
          this.quantities[id] = 1;
          this.activeId = id;
        }

        this.render();
      }

      selectFlavour(id, initial = false) {
        const card = this.cards.find((c) => c.dataset.variantId === id);
        if (!card || card.dataset.available !== 'true') return;
        if (!this.quantities[id]) this.quantities[id] = 1;
        this.activeId = id;
        this.render();
      }

      changeQty(delta) {
        if (!this.activeId) return;
        const next = (this.quantities[this.activeId] || 0) + delta;
        if (next <= 0) {
          delete this.quantities[this.activeId];
          const remaining = Object.keys(this.quantities);
          this.activeId = remaining[remaining.length - 1] || null;
        } else {
          this.quantities[this.activeId] = next;
        }
        this.render();
      }

      formatMoney(cents) {
        try {
          return new Intl.NumberFormat(this.locale, {
            style: 'currency',
            currency: this.currency,
          }).format(cents / 100);
        } catch (e) {
          return `£${(cents / 100).toFixed(2)}`;
        }
      }

      getActiveCard() {
        return this.cards.find((card) => card.dataset.variantId === this.activeId) || null;
      }

      render() {
        this.cards.forEach((card) => {
          const id = card.dataset.variantId;
          card.classList.toggle('is-selected', Boolean(this.quantities[id]));
          card.setAttribute('aria-pressed', this.quantities[id] ? 'true' : 'false');
        });

        const activeCard = this.getActiveCard();
        const qty = activeCard ? this.quantities[this.activeId] || 0 : 0;

        if (this.selectedName) {
          this.selectedName.textContent = activeCard
            ? activeCard.dataset.title
            : this.dataset.emptySelection || 'None selected';
        }

        if (this.selectedSwatch) {
          const showSwatch = this.dataset.showSwatch !== 'false';
          const swatch = (activeCard && activeCard.dataset.swatch) || '';
          if (showSwatch && swatch) {
            this.selectedSwatch.hidden = false;
            this.selectedSwatch.style.background = swatch;
            this.selectedSwatch.style.opacity = '1';
          } else {
            this.selectedSwatch.hidden = true;
            this.selectedSwatch.style.background = 'transparent';
            this.selectedSwatch.style.opacity = '0';
          }
        }

        if (this.qtyValue) this.qtyValue.textContent = String(qty);
        if (this.qtyMinus) this.qtyMinus.disabled = !activeCard;
        if (this.qtyPlus) this.qtyPlus.disabled = !activeCard;

        let total = 0;
        Object.keys(this.quantities).forEach((id) => {
          const card = this.cards.find((c) => c.dataset.variantId === id);
          if (!card) return;
          total += Number(card.dataset.price || 0) * this.quantities[id];
        });

        if (this.totalValue) this.totalValue.textContent = this.formatMoney(total);
        if (this.submitBtn) this.submitBtn.disabled = total <= 0;
        this.clearError();
      }

      clearError() {
        if (this.errorEl) {
          this.errorEl.hidden = true;
          this.errorEl.textContent = '';
        }
      }

      showError(message) {
        if (!this.errorEl) return;
        this.errorEl.hidden = false;
        this.errorEl.textContent = message;
      }

      async addToCart() {
        const items = Object.keys(this.quantities).map((id) => ({
          id: Number(id),
          quantity: this.quantities[id],
        }));

        if (!items.length) return;

        this.clearError();
        this.submitBtn.classList.add('is-loading');
        this.submitBtn.setAttribute('aria-disabled', 'true');

        const sections =
          this.cart && typeof this.cart.getSectionsToRender === 'function'
            ? this.cart.getSectionsToRender().map((section) => section.id)
            : [];

        if (this.cart?.setActiveElement) {
          this.cart.setActiveElement(document.activeElement);
        }

        try {
          const body = {
            items,
            sections: sections.join(','),
            sections_url: window.location.pathname,
          };

          const response = await fetch(window.routes.cart_add_url, {
            method: 'POST',
            headers: {
              Accept: 'application/json',
              'Content-Type': 'application/json',
              'X-Requested-With': 'XMLHttpRequest',
            },
            body: JSON.stringify(body),
          });
          const data = await response.json();

          if (data.status) {
            this.showError(data.description || data.message || 'Unable to add to cart.');
            return;
          }

          if (!this.cart) {
            window.location = window.routes.cart_url;
            return;
          }

          if (!data.sections || !data.sections['cart-drawer']) {
            // Fallback: fetch drawer HTML if bundled sections missing
            const sectionResponse = await fetch(`?sections=cart-drawer,cart-icon-bubble`);
            const sectionData = await sectionResponse.json();
            data.sections = sectionData;
          }

          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'flavour-add-to-cart',
              cartData: data,
            });
          }

          this.cart.renderContents(data);

          // Same as Dawn product-form.js — without this, .is-empty hides cart line items
          if (this.cart.classList.contains('is-empty')) {
            this.cart.classList.remove('is-empty');
          }
        } catch (error) {
          console.error(error);
          this.showError('Unable to add to cart. Please try again.');
        } finally {
          this.submitBtn.classList.remove('is-loading');
          this.submitBtn.removeAttribute('aria-disabled');
          this.render();
        }
      }
    }
  );
}
